import _classnames from 'clsx';
import PropTypes from 'prop-types';
import React, { Component, forwardRef } from 'react';
import { mergeRefs } from '../../../common/js/utilities.ts';
import Icon from '../../icons/Icon/Icon';

/**
 * This class component includes a checkbox with a label
 */
class Checkbox extends Component {
  constructor(props) {
    super(props);

    this.checkbox = React.createRef();
  }

  componentDidUpdate() {
    const { indeterminate } = this.props;

    if (this.checkbox.current) {
      this.checkbox.current.indeterminate = indeterminate;
    }
  }

  setCheckboxRef = (element) => {
    this.checkbox = element;
  };

  render() {
    const {
      classes,
      checked,
      disabled,
      indeterminate,
      innerRef,
      inputProps,
      onChange,
      selected,
      textLabel,
      name,
    } = this.props;

    const internalInputProps = {
      checked,
      className: 'Checkbox__input',
      disabled,
      name,
      onChange,
      indeterminate: indeterminate ? 'true' : 'false',
      type: 'checkbox',
      ...inputProps,
    };

    const renderCheckbox = (
      <div className="Checkbox__custom-container">
        <input ref={mergeRefs(this.setCheckboxRef, innerRef)} {...internalInputProps} />
        <span
          className={_classnames('Checkbox__custom', {
            'Checkbox__custom--active': checked || indeterminate,
            'Checkbox__custom--disabled': disabled,
            'Checkbox__custom--selected': selected,
          })}
        >
          <Icon
            classes="Checkbox__icon"
            name={indeterminate ? 'Minus' : 'Checkmark--single'}
            size="micro"
          />
        </span>
      </div>
    );

    return (
      <div
        className={_classnames('Checkbox', classes, {
          'Checkbox--disabled': disabled,
        })}
      >
        {textLabel ? (
          <label
            htmlFor={name}
            className={_classnames('Checkbox__label', {
              'Checkbox__label--selected': selected,
            })}
          >
            {renderCheckbox}
            <div className="Checkbox__text-label">{textLabel}</div>
          </label>
        ) : (
          <div
            className={_classnames('Checkbox__label', {
              'Checkbox__label--selected': selected,
            })}
          >
            {renderCheckbox}
          </div>
        )}
      </div>
    );
  }
}

Checkbox.propTypes = {
  /**
   * Set to true to show the checkbox is currently checked
   */
  checked: PropTypes.bool,
  /**
   * A space-separated list of classes to apply to the outer div wrapping the component
   */
  classes: PropTypes.string,
  /**
   * Disabled state applies to both checkbox and label
   */
  disabled: PropTypes.bool,
  /**
   * Indeterminate state applies to the checkbox and gets set in the componentDidUpdate
   */
  indeterminate: PropTypes.bool,
  /**
   * Any additional props to apply to the checkbox input element
   */
  // eslint-disable-next-line react/forbid-prop-types
  inputProps: PropTypes.object,
  /**
   * Applied as a prop to the checkbox input element
   */
  name: PropTypes.string,
  /**
   * Callback function applied as a prop to the checkbox input element
   */
  onChange: PropTypes.func,
  /**
   * Set to true to display the hover state for the checkbox (light gray check appears when
   * hovering anywhere over the label or checkbox)
   */
  selected: PropTypes.bool,
  /**
   * The checkbox label text
   */
  textLabel: PropTypes.string,
  /**
   * Inner Ref
   */
  innerRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

Checkbox.defaultProps = {
  checked: null,
  classes: '',
  disabled: false,
  inputProps: {},
  name: null,
  onChange: () => {},
  selected: false,
  textLabel: null,
  indeterminate: false,
  innerRef: null,
};

export default forwardRef((props, ref) => <Checkbox innerRef={ref} {...props} />);
